// ProjectCard.js
import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiFolder, FiCalendar, FiEye } from 'react-icons/fi';
import { FaStar, FaGithub, FaLinkedin } from 'react-icons/fa';

const truncateText = (text, limit = 120) => {
  return text.length > limit ? text.substring(0, limit) + '...' : text;
};

const ProjectCard = ({ project }) => {
  const isLinkedIn = project.github && project.github.includes('linkedin.com');

  return (
    <Link to={`/project/${project.id}`} className="block h-full">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        whileHover={{ y: -6, transition: { duration: 0.2 } }}
        viewport={{ once: true }}
        className={`relative group rounded-xl glass-panel p-6 h-full overflow-hidden transition-all duration-300 flex flex-col justify-between ${
          project.highlight
            ? 'border-indigo-500/30 shadow-[0_0_15px_rgba(99,102,241,0.05)]'
            : 'border-white/10'
        } hover:border-indigo-400/50 hover:shadow-[0_0_30px_rgba(99,102,241,0.15)]`}
      >
        {/* Glow Effect Overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 via-cyan-500/0 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
        
        {/* Smooth Click Overlay Badge on Hover */}
        <div className="absolute inset-0 bg-slate-950/40 backdrop-blur-[1.5px] opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-center justify-center pointer-events-none z-20">
          <div className="px-4 py-2 bg-indigo-600/90 border border-indigo-400/30 rounded-lg text-xs font-mono font-bold tracking-wide text-white shadow-[0_0_15px_rgba(99,102,241,0.4)] animate-float flex items-center gap-1.5">
            <FiEye className="text-cyan-300" />
            <span>Click to view full details</span>
          </div>
        </div>

        {/* Featured Ribbon / Star badge */}
        {project.highlight && (
          <div className="absolute top-0 right-0 mt-3 mr-3 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-indigo-500/10 border border-indigo-500/30 text-indigo-300 text-[10px] font-medium tracking-wider uppercase font-mono">
            <FaStar className="text-yellow-400 text-xs animate-pulse" />
            Featured
          </div>
        )}

        {/* Card Body */}
        <div className="relative z-10 flex flex-col h-full justify-between">
          <div>
            {/* Header (Folder + Title) */}
            <div className="flex items-start gap-3 mb-3 pr-20">
              <div className={`p-2 rounded-lg ${project.highlight ? 'bg-indigo-500/10 text-indigo-400' : 'bg-white/5 text-slate-400'} group-hover:text-cyan-400 transition-colors duration-300`}>
                <FiFolder size={20} />
              </div>
              <h3 className="text-lg font-bold text-slate-100 group-hover:text-white group-hover:text-accent-gradient transition-colors duration-300 leading-snug">
                {project.name}
              </h3>
            </div>

            {/* Description */}
            <p className="text-sm text-slate-400 leading-relaxed mb-5 group-hover:text-slate-300 transition-colors duration-300">
              {truncateText(project.description)}
            </p>
          </div>

          <div>
            {/* Tags */}
            <div className="flex flex-wrap gap-1.5 mb-5">
              {project.tags.slice(0, 5).map((tag, idx) => (
                <span
                  key={idx}
                  className="text-[11px] font-mono font-medium px-2.5 py-0.5 rounded-full bg-slate-800/60 border border-slate-700/50 text-slate-300 group-hover:border-indigo-500/30 group-hover:text-indigo-200 transition-all duration-300"
                >
                  {tag}
                </span>
              ))}
              {project.tags.length > 5 && (
                <span className="text-[11px] font-mono px-2 py-0.5 rounded-full bg-slate-800/40 text-slate-500">
                  +{project.tags.length - 5} more
                </span>
              )}
            </div>

            {/* Footer Actions */}
            <div className="flex items-center justify-between pt-4 border-t border-white/5 mt-auto">
              {/* Timeline Indicator */}
              <span className="text-[11px] font-mono text-slate-500 flex items-center gap-1.5">
                <FiCalendar size={12} className="text-slate-600" />
                <span>{project.startDate}</span>
              </span>

              {/* Dynamic link button (GitHub vs. LinkedIn vs. Empty) */}
              {project.github && (
                <a
                  href={project.github}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="text-slate-400 hover:text-white py-1 px-2 rounded-md hover:bg-white/5 border border-white/5 hover:border-white/10 transition-all duration-200 relative z-30 flex items-center gap-1.5 text-[11px] font-mono"
                  title={isLinkedIn ? 'View Post on LinkedIn' : 'View Repository on GitHub'}
                >
                  {isLinkedIn ? (
                    <>
                      <FaLinkedin size={13} className="text-[#0a66c2]" />
                      <span>LinkedIn</span>
                    </>
                  ) : (
                    <>
                      <FaGithub size={13} />
                      <span>Repository</span>
                    </>
                  )}
                </a>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </Link>
  );
};

export default ProjectCard;
